/**
 * CapturesGallery v2 — a job's rig captures, ported from the locked mockup.
 * Reads the live `jobs/{jobId}/captureAttempts` records (the authoritative rig
 * capture docs). Tiles: avatar + name + SN + a status dot, the upload state
 * the capture PC's uploader reached (uploading / in the cloud / discarded,
 * from the attempt's `upload` field), and the guest delivery.
 *
 * Discard (docs/ingest/14-capture-upload-contract.md), with a confirm step:
 * the capture is not uploaded, and a cloud copy already made is deleted
 * (discardCapture callable). A cloud delete that has to wait (an upload link
 * handed out in the last minutes) or failed is finished by the Suite within
 * about 20 minutes; the tile says so and offers the delete again. The
 * uploader moves its own copy into its Discarded folder on the capture PC;
 * nothing is deleted there. Name and status edits stay on the existing
 * surfaces. The guest page never shows a discarded capture, so Discard also
 * takes an emailed capture off its guests' pages: the confirm step then says
 * how many guests it was sent to (emailedCountOf).
 *
 * Guest delivery (v2, docs/ingest/15-guest-delivery-v2-contract.md): once the
 * processing machine reports a capture, the server mirrors its delivery onto
 * the record as `delivery` (v: 2): the capture's status over all its members
 * and one row per member (status, masked address, dates, last try;
 * functions/src/services/delivery.ts mirrorOf). A tile shows one line per
 * member with "Send" / "Send again" for that member, and the bar sends every
 * member still waiting, across all captures. Each email goes to that
 * member's one page, which holds every capture of them. Sending goes through
 * the sendGuestDelivery callable; this view never writes to Firestore.
 * canSend false (an event account, docs/ops/event-role.md: the server refuses
 * it sendGuestDelivery): the lines show, with no Send button and no bulk send.
 *
 * The first-email gate (Ivin's review, DeliveryReview.jsx): a capture whose
 * model is not approved yet says so and offers no send; a guest under 18
 * whose address an admin has not confirmed as the parent's or guardian's
 * says "needs guardian check" and is left out of "Send all ready" (the server
 * refuses both anyway).
 *
 * A guest who asks to be deleted (admins only, canDeleteGuest): each guest's
 * line whose page has not ended offers "Delete this guest's delivery (at
 * their request)", with a confirm step that says what goes and needs DELETE
 * typed (the deleteGuestDelivery callable: their page, download code and
 * registration details; a capture shared with others stays for them).
 *
 * For admins only (canDownload), a capture In the cloud also has "Download
 * for processing" (CaptureDownload.jsx): its photos and info file, to process
 * them on a studio computer.
 */
import React, { useMemo, useState } from "react";
import { useFirestoreCollection } from "../../../../lib/useFirestore.js";
import { rigv2 } from "./rigv2Callables.js";
import { deliveryOf, needsGuardianCheck } from "./deliveryReviewModel.js";
import GuestPageButton, { hasGuestPage } from "./GuestPageButton.jsx";
import { DownloadControl, useCaptureDownload } from "./CaptureDownload.jsx";

export { deliveryOf };

// Map the live capture-attempt state → a coarse display status (mockup vocab).
const statusOf = (a) => {
  const s = a.state ?? a.status;
  if (s === "complete") return "complete";
  if (s === "abandoned" || s === "failed") return "flagged";
  return "progress";
};
const nameOf = (a) => (a.subjectSnapshot?.displayNameAtFire || a.subjectSnapshot?.subjectName || a.displayName || a.subjectName || "").trim();
// A group capture's member by roster id (the lead is subjectSnapshot).
const memberNameOf = (a, rosterEntryId) => {
  const m = Array.isArray(a.members) ? a.members.find((x) => x?.rosterEntryId === rosterEntryId) : null;
  if (m) return (m.displayNameAtFire || m.subjectName || "").trim();
  return a.subjectSnapshot?.rosterEntryId === rosterEntryId ? nameOf(a) : "";
};
const snOf = (a) => a.id ?? a.sn ?? "";
const initials = (name) => (name ? name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("") : "·");
// Stable per-capture avatar tint.
const PALETTES = [["#E8F1F8", "#5B7C99"], ["#F0F4E8", "#7A9659"], ["#F8EFE0", "#A78050"], ["#EFE4F0", "#8C6FA5"], ["#F2E8E8", "#A06868"]];
const paletteFor = (key) => {
  let h = 0; for (const ch of String(key || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTES[h % PALETTES.length];
};

/* ── guest delivery ── */
// The roster entries of guests under 18 whose address is not confirmed as the
// parent's or guardian's: nothing may be sent to them yet.
export const guardianBlockedOf = (roster) => new Set((roster ?? []).filter(needsGuardianCheck).map((r) => r.id));
// The members of a capture who can be sent it now ("ready" covers a member
// whose last send failed: the server keeps them ready), less any waiting for
// the guardian check.
// (blocked: a Set of roster entry ids; anything else, like an array filter's
// index, counts as none.)
const NONE = new Set();
const asSet = (s) => (s instanceof Set ? s : NONE);
export const readyMembersOf = (a, blocked = NONE) =>
  (deliveryOf(a)?.members ?? []).filter((m) => m.status === "ready" && !asSet(blocked).has(m.rosterEntryId));
// A capture waiting for Ivin's review (status "review") is never ready to send.
export const isReadyToSend = (a, blocked = NONE) => {
  const d = deliveryOf(a);
  return Boolean(d) && d.status !== "deleted" && d.status !== "review" && readyMembersOf(a, asSet(blocked)).length > 0;
};
const shortDate = (ms) =>
  ms ? new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/New_York" }) : "";

// The callable's refusal codes (HttpsError message) in the words the crew see.
export const sendErrorText = (e) => {
  const code = String(e?.message || "");
  if (code === "no_email") return "No email on file. An admin fixes it with Fix address on the Never emailed tab, then send.";
  if (code === "deleted") return "The files were deleted.";
  if (code === "not_found") return "Not processed yet.";
  if (code === "ended") return "This guest deleted their page, or its time is over.";
  if (code === "discarded") return "This capture was discarded. It is never sent.";
  if (code === "send_in_progress") return "Already sending to this guest. Try again in a few minutes.";
  if (code === "already_sent") return "Already sent, or its send is not confirmed yet. Use Send again on this line.";
  if (code === "incomplete") return "Files missing from processing. Process it again.";
  if (code === "expired") return "This guest's 3 months are over.";
  if (code === "too_many_links") return "This guest has had too many emails. Write to them by hand.";
  if (code === "not_approved") return "Not approved yet. Ivin approves each model on the Review tab first.";
  if (code === "needs_guardian") return "Under 18: an admin confirms the parent's or guardian's address on the Review tab first.";
  if (/Requires role/.test(code)) return "Only an admin or rig operator can send.";
  return "Could not send. Try again.";
};

/** One member's delivery of one capture. guardian: this guest is under 18 and waits for the guardian check. */
export function MemberLine({ m, name, guardian = false }) {
  if (!m) return null;
  const who = name ? `${name}: ` : "";
  if (m.status === "ended") return <span className="rigv2-dlv deleted">{who}Page ended (deleted or expired)</span>;
  if (guardian && m.status !== "sent") {
    return <span className="rigv2-dlv warn">{who}Needs guardian check (an admin confirms the address on the Review tab)</span>;
  }
  if (m.status === "no_email") return <span className="rigv2-dlv warn">{who}No email on file</span>;
  const last = m.lastSend ?? null;
  const lastTo = last?.toMasked ? ` to ${last.toMasked}` : "";
  if (m.status === "sent") {
    const downloadOver = m.downloadUntil && m.downloadUntil < Date.now();
    // No link works: the address was changed and no email to the new one
    // went out yet. The dates keep running.
    const noLink = m.hasLink === false;
    // The latest try, told apart from what came before: a refused one leaves
    // the earlier emails (and their links) as they were; an unconfirmed one
    // after a confirmed email may or may not have gone out.
    const lastFailed = last?.status === "failed" && last.lastError;
    const lastUnsure = last?.status === "uncertain" && !m.unconfirmed;
    const to = m.emailMasked ? ` to ${m.emailMasked}` : "";
    return (
      <span className={`rigv2-dlv ${noLink || m.unconfirmed || lastFailed || lastUnsure ? "warn" : "sent"}`}>
        {who}{noLink ? "No working link. Send again." :
          m.unconfirmed ? `Not confirmed: may have gone${to} on ${shortDate(m.emailedAt)}` : `Sent ${shortDate(m.confirmedAt ?? m.emailedAt)}${to}`}
        <span className="rigv2-dlv-dates">
          {downloadOver ? "Download ended" : `Download until ${shortDate(m.downloadUntil)}`} · View until {shortDate(m.viewUntil)}
        </span>
        {lastFailed && <span className="rigv2-dlv-dates">Last try{lastTo} failed ({last.lastError})</span>}
        {lastUnsure && <span className="rigv2-dlv-dates">Last try{lastTo} not confirmed: the email may have gone out.</span>}
        {guardian && <span className="rigv2-dlv-dates">The address changed: it needs the guardian check again before another email.</span>}
      </span>
    );
  }
  const failed = last?.status === "failed" && last.lastError;
  return (
    <span className={`rigv2-dlv ${failed ? "warn" : "ready"}`}>
      {who}{failed ? `Send failed${lastTo} (${last.lastError})` : "Ready to send"}
    </span>
  );
}

/* ── a guest who asks to be deleted (admins only) ── */
// The deleteGuestDelivery callable (functions/src/services/delivery.ts
// deleteAtGuestRequest): the same as the guest's own "Delete everything now",
// for a guest who asked New York Capture (by email, say). Admin only on the
// server; the button shows only to admins (canDeleteGuest).
export const GUEST_DELETE_LABEL = "Delete this guest's delivery (at their request)";
export const GUEST_DELETE_TEXT =
  "This deletes their page, download code and their name and email. Shared captures stay for other people in the group.";
// The word typed to confirm, and whether what was typed is it (case and
// surrounding spaces do not matter).
export const GUEST_DELETE_WORD = "DELETE";
export const guestDeleteReady = (typed) => String(typed ?? "").trim().toUpperCase() === GUEST_DELETE_WORD;
// The callable's answer in plain words.
export const guestDeleteResultText = (r) => {
  if (!r) return "";
  const first = r.alreadyEnded ?
    "Their page had already ended. Their details are deleted." :
    "Deleted: their page, download code, name and email are gone.";
  const caps = r.capturesDeleted > 0 ?
    ` ${r.capturesDeleted === 1 ? "1 capture nobody else had was" : `${r.capturesDeleted} captures nobody else had were`} deleted too.` : "";
  const later = r.scrub === "pending" ? " Their name on the capture records goes in tonight's cleanup." : "";
  return `${first}${caps}${later}`;
};
export const guestDeleteErrorText = (e) => {
  const code = String(e?.message || "");
  if (code === "not_found") return "Not found: this guest has no page or registration on this job.";
  if (/Requires role/.test(code)) return "Only an admin can delete a guest's delivery.";
  // A timeout may still have finished on the server: the line says so.
  return `The delete did not finish (${code || "no answer"}). If the line now says Page ended, or the capture says Deleted, it went through; otherwise try again.`;
};

// The guest delete answers kept for one capture's lines ("sn|rosterEntryId"
// keys), in the order they came: shown on the tile even after its lines go.
export const guestDeleteNotesOf = (notes, sn) =>
  Object.entries(notes ?? {}).filter(([k, v]) => v && k.startsWith(`${sn}|`));

/**
 * The last guest delete's answer, above the captures: it stays whatever the
 * search or filter shows (the deleted guest's capture may no longer match
 * them), until dismissed. Pure given its props.
 */
export function GuestDeleteBanner({ result, onDismiss }) {
  if (!result?.note) return null;
  return (
    <div className="rigv2-dlv-note rigv2-gal-delete-note" role="status">
      <span>Guest delete, capture {result.sn}: {result.note}</span>
      <button type="button" className="rigv2-tile-btn" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

// The delete itself, only once DELETE is typed: whether the callable was
// called, whether the confirm step closes (it went through), and the line's
// words.
export const runGuestDeleteCall = async ({ deleteGuest, jobId, rosterEntryId, typed }) => {
  if (!guestDeleteReady(typed)) return { called: false, closed: false, note: "" };
  try {
    const r = await deleteGuest({ jobId, rosterEntryId });
    return { called: true, closed: true, note: guestDeleteResultText(r) };
  } catch (e) {
    return { called: true, closed: false, note: guestDeleteErrorText(e) };
  }
};

/**
 * The admin's delete for one guest, on their line. Pure given its props:
 * closed, the button; open, the plain words, a box to type DELETE in, and
 * Delete (only once DELETE is typed) / Keep. note: the answer or the error.
 */
export function GuestDeleteControl({ open, typed = "", busy = false, note = "", onOpen, onType, onConfirm, onCancel }) {
  if (!open) {
    return (
      <>
        <button type="button" className="rigv2-tile-btn danger" disabled={busy} onClick={onOpen}>{GUEST_DELETE_LABEL}</button>
        {note && <span className="rigv2-dlv-note">{note}</span>}
      </>
    );
  }
  const ready = guestDeleteReady(typed);
  return (
    <div className="rigv2-tile-actions rigv2-guest-delete">
      <span className="rigv2-tile-confirm warn">{GUEST_DELETE_TEXT}</span>
      <label className="rigv2-tile-confirm">
        Type {GUEST_DELETE_WORD} to confirm{" "}
        <input type="text" value={typed} autoComplete="off" disabled={busy} aria-label={`Type ${GUEST_DELETE_WORD} to confirm`}
          onChange={(e) => onType?.(e.target.value)} />
      </label>
      <button type="button" className="rigv2-tile-btn danger" disabled={!ready || busy} onClick={onConfirm}>{busy ? "Deleting..." : "Delete"}</button>
      <button type="button" className="rigv2-tile-btn" disabled={busy} onClick={onCancel}>Keep</button>
      {note && <span className="rigv2-dlv-note">{note}</span>}
    </div>
  );
}

const FILTERS = [["all", "All"], ["complete", "Complete"], ["flagged", "Flagged"], ["progress", "In progress"], ["ready", "Ready to send"]];

// The upload tag for a capture, from its attempt's `upload` mirror. Pure.
export function uploadTagOf(a) {
  const u = a?.upload;
  if (!u || typeof u !== "object") return null;
  if (u.state === "uploading") return { cls: "uploading", text: "Uploading" };
  if (u.state === "verified") {
    // Missing cameras, or cameras not known (null: dataset.json did not say
    // which camera took every photo): the tile says how many photos went up.
    const countIt = u.missingCameras === null || (Array.isArray(u.missingCameras) && u.missingCameras.length > 0);
    return { cls: "verified", text: countIt ? `In the cloud, ${u.photoCount ?? "?"} photos` : "In the cloud" };
  }
  // The guest's delivery ended before these photos were verified: they are
  // never uploaded (docs/ingest/14-capture-upload-contract.md).
  if (u.state === "closed") return { cls: "discarded", text: "Not uploaded: delivery ended" };
  if (u.state === "discarded") {
    return u.cloudDeleted === false ?
      { cls: "discarded", text: "Discarded, deleting the cloud copy", retry: true } :
      { cls: "discarded", text: "Discarded" };
  }
  return null;
}

export const DISCARD_CONFIRM_TEXT = "Discard this capture? It will not be uploaded, and a copy already in the cloud is deleted.";

// How many guests may have this capture on their page now: an email about it
// may have reached them (emailedAt, set for an unconfirmed email too), and
// their page has not ended (deleted by the guest, or its time over).
export const emailedCountOf = (a) =>
  (deliveryOf(a)?.members ?? []).filter((m) => Boolean(m?.emailedAt) && m.status !== "ended").length;

// The confirm step's words for those guests (none: nothing is added).
export const discardSentText = (n) => {
  if (!(n > 0)) return "";
  return n === 1 ?
    "This capture was already sent to 1 guest. Discarding removes it from their page." :
    `This capture was already sent to ${n} guests. Discarding removes it from their pages.`;
};

/**
 * The Discard control of one tile. Pure given its props: confirming shows
 * the question with Discard / Keep; otherwise the Discard button (or, for a
 * discarded capture whose cloud copy could not be deleted, a retry). sentTo:
 * how many guests it was emailed to (emailedCountOf); when any, the confirm
 * step also says Discard takes it off their pages.
 */
export function DiscardControl({ tag, confirming, busy, error, sentTo = 0, onAsk, onConfirm, onCancel }) {
  if (tag?.cls === "discarded" && !tag.retry) return null;
  return (
    <div className="rigv2-tile-actions">
      {confirming ? (
        <>
          <span className="rigv2-tile-confirm">{DISCARD_CONFIRM_TEXT}</span>
          {sentTo > 0 && <span className="rigv2-tile-confirm warn">{discardSentText(sentTo)}</span>}
          <button type="button" className="rigv2-tile-btn danger" disabled={busy} onClick={onConfirm}>{busy ? "Discarding..." : "Discard"}</button>
          <button type="button" className="rigv2-tile-btn" disabled={busy} onClick={onCancel}>Keep</button>
        </>
      ) : tag?.retry ? (
        <button type="button" className="rigv2-tile-btn" disabled={busy} onClick={onConfirm}>{busy ? "Deleting..." : "Delete cloud copy"}</button>
      ) : (
        <button type="button" className="rigv2-tile-btn" onClick={onAsk}>Discard</button>
      )}
      {error && <span className="rigv2-tile-err" role="alert">{error}</span>}
    </div>
  );
}

// canSend false: no Send button and no bulk send (an event account).
// canDeleteGuest (admins only): each guest's line offers the delete a guest
// asked for (GuestDeleteControl). canDownload (admins only): Download for
// processing on a capture In the cloud (CaptureDownload.jsx).
export default function CapturesGallery({
  jobId, discard = rigv2.discardCapture, canSend = true, canDeleteGuest = false, deleteGuest = rigv2.deleteGuestDelivery,
  canDownload = false, getDownloadLinks = rigv2.getCaptureDownloadLinks, canOpenGuestPage = false,
}) {
  const { data: captures, loading } = useFirestoreCollection(jobId ? `jobs/${jobId}/captureAttempts` : null);
  // Join the roster to surface the guest/crew organizational label on each
  // captured subject (the captureAttempt itself doesn't carry the label). The
  // label is NOT billing — it's a plain tag (and drives a future guest/crew folder).
  const { data: roster } = useFirestoreCollection(jobId ? `jobs/${jobId}/rosterEntries` : null);
  const kindById = useMemo(() => {
    const m = {};
    for (const r of roster) { const k = r.customFields?.kind; if (k) m[r.id] = k; }
    return m;
  }, [roster]);
  // Guests under 18 waiting for the guardian check: nothing is sent to them.
  const blocked = useMemo(() => guardianBlockedOf(roster), [roster]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [confirmSn, setConfirmSn] = useState(null);
  const [busySn, setBusySn] = useState(null);
  const [errors, setErrors] = useState({});
  // Per capture-and-member send state ("sn|rosterEntryId"): sending, or the
  // last refusal's words.
  const [busy, setBusy] = useState({});
  const [notes, setNotes] = useState({});
  const [bulk, setBulk] = useState(null);
  // The guest delete: the line whose confirm step is open ("sn|rosterEntryId"),
  // what was typed there, the line deleting now, and each line's answer.
  const [delKey, setDelKey] = useState(null);
  const [delTyped, setDelTyped] = useState("");
  const [delBusy, setDelBusy] = useState(null);
  const [delNotes, setDelNotes] = useState({});
  // The last delete's answer, shown above the captures (GuestDeleteBanner).
  const [lastDelete, setLastDelete] = useState(null);
  const download = useCaptureDownload({ jobId, getLinks: getDownloadLinks });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...captures]
      .filter((a) => (filter === "all" ? true : filter === "ready" ? isReadyToSend(a, blocked) : statusOf(a) === filter))
      .filter((a) => !q || [nameOf(a), snOf(a), ...(Array.isArray(a.members) ? a.members.map((m) => m?.displayNameAtFire || m?.subjectName || "") : [])]
        .join(" ").toLowerCase().includes(q))
      .sort((a, b) => (b.firedAt ?? b.triggeredAt ?? 0) - (a.firedAt ?? a.triggeredAt ?? 0));
  }, [captures, search, filter, blocked]);
  // Every (capture, member) still waiting for their first email of it.
  const ready = useMemo(() => captures.filter((a) => isReadyToSend(a, blocked))
    .flatMap((a) => readyMembersOf(a, blocked).map((m) => ({ a, m }))), [captures, blocked]);

  // One send to one member. Returns "sent", "unconfirmed" (the provider
  // never answered: it may have gone out) or "failed". The bulk send only
  // ever makes a first email (the server refuses one sent or unresolved).
  const sendOne = async (a, m, { firstSendOnly }) => {
    const sn = snOf(a);
    const key = `${sn}|${m.rosterEntryId}`;
    setBusy((b) => ({ ...b, [key]: true }));
    setNotes((n) => ({ ...n, [key]: "" }));
    try {
      const r = await rigv2.sendGuestDelivery({ jobId, sn, rosterEntryId: m.rosterEntryId, firstSendOnly });
      if (r?.status === "unconfirmed") {
        setNotes((n) => ({ ...n, [key]: "Not confirmed: the email may have gone out." }));
      } else if (r?.status !== "sent") {
        setNotes((n) => ({ ...n, [key]: "The email did not go out. Try again." }));
      }
      return r?.status === "sent" || r?.status === "unconfirmed" ? r.status : "failed";
    } catch (e) {
      setNotes((n) => ({ ...n, [key]: sendErrorText(e) }));
      return "failed";
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const sendMember = (a, m) => {
    const question = m.unconfirmed ?
      "Send again? If the first email arrived, the guest may get a second one. Both links open the same page." :
      "Send again? This emails a new link to the same page; the link already sent keeps working.";
    if (m.status === "sent" && !window.confirm(question)) return;
    sendOne(a, m, { firstSendOnly: false });
  };
  const anyBusy = Object.values(busy).some(Boolean);

  const sendAllReady = async () => {
    const list = [...ready];
    if (!list.length || !window.confirm(`Send ${list.length} guest ${list.length === 1 ? "email" : "emails"} now?`)) return;
    let ok = 0;
    let unconfirmed = 0;
    for (let i = 0; i < list.length; i++) {
      setBulk({ done: i, total: list.length, ok, unconfirmed });
      const r = await sendOne(list[i].a, list[i].m, { firstSendOnly: true });
      if (r === "sent") ok++;
      if (r === "unconfirmed") unconfirmed++;
    }
    setBulk({ done: list.length, total: list.length, ok, unconfirmed, finished: true });
  };

  const runGuestDelete = async (key, rosterEntryId) => {
    if (delBusy || !guestDeleteReady(delTyped)) return;
    setDelBusy(key);
    setDelNotes((n) => ({ ...n, [key]: "" }));
    try {
      const r = await runGuestDeleteCall({ deleteGuest, jobId, rosterEntryId, typed: delTyped });
      if (r.closed) {
        setDelKey(null);
        setDelTyped("");
      }
      setDelNotes((n) => ({ ...n, [key]: r.note }));
      setLastDelete(r.note ? { sn: key.split("|")[0], note: r.note } : null);
    } finally {
      setDelBusy(null);
    }
  };

  const runDiscard = async (sn) => {
    if (busySn) return;
    setBusySn(sn);
    setErrors((e) => ({ ...e, [sn]: null }));
    try {
      const r = await discard({ jobId, sn });
      setConfirmSn(null);
      // Only a step that failed is worth a word; a delete that waits for an
      // upload link to expire finishes by itself.
      if (r && r.retryNeeded === true) {
        setErrors((e) => ({ ...e, [sn]: "Discarded. The cloud copy could not be deleted yet; the Suite tries again by itself, or press Delete cloud copy." }));
      }
    } catch (err) {
      setErrors((e) => ({ ...e, [sn]: `Discard did not go through: ${err?.message ?? String(err)}` }));
    } finally {
      setBusySn(null);
    }
  };

  return (
    <div className="rigv2-gallery">
      <div className="rigv2-gal-bar">
        <span className="rigv2-gal-search">
          <input placeholder="Search name or SN…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </span>
        {FILTERS.map(([id, label]) => (
          <button key={id} type="button" className={`rigv2-chip ${filter === id ? "on" : ""}`} onClick={() => setFilter(id)}>{label}</button>
        ))}
        <span className="rigv2-gal-count">{rows.length} {rows.length === 1 ? "capture" : "captures"}</span>
        {canSend && (ready.length > 0 || bulk) && (
          <span className="rigv2-gal-send">
            {bulk && !bulk.finished ? (
              <span className="rigv2-gal-progress">Sending {bulk.done + 1} of {bulk.total}…</span>
            ) : (
              <>
                {bulk?.finished && (
                  <span className="rigv2-gal-progress">
                    Sent {bulk.ok} of {bulk.total}.
                    {bulk.unconfirmed ? ` ${bulk.unconfirmed} not confirmed.` : ""}
                    {bulk.ok < bulk.total ? " The rest say why on their tiles." : ""}
                  </span>
                )}
                {ready.length > 0 && (
                  <button type="button" className="rigv2-btn primary small" disabled={anyBusy} onClick={sendAllReady}>Send all ready ({ready.length})</button>
                )}
              </>
            )}
          </span>
        )}
      </div>

      {canDeleteGuest && <GuestDeleteBanner result={lastDelete} onDismiss={() => setLastDelete(null)} />}

      {loading ? (
        <div className="rigv2-empty">Loading captures…</div>
      ) : rows.length === 0 ? (
        <div className="rigv2-empty">{captures.length === 0 ? "No captures yet for this job." : "No captures match."}</div>
      ) : (
        <div className="rigv2-grid">
          {rows.map((a) => {
            const name = nameOf(a);
            const sn = snOf(a);
            const reId = a.subjectSnapshot?.rosterEntryId;
            const kind = reId ? kindById[reId] : null;
            const [bg, fg] = paletteFor(reId ?? sn);
            const tag = uploadTagOf(a);
            const d = deliveryOf(a);
            const group = (d?.members.length ?? 0) > 1;
            return (
              <div key={sn} className="rigv2-tile">
                <span className="rigv2-tile-thumb" style={{ background: bg, color: fg }}>
                  {initials(name)}
                  <span className={`rigv2-tile-status ${statusOf(a)}`} title={a.state ?? a.status ?? ""} />
                </span>
                <span className={`rigv2-tile-name ${name ? "" : "unnamed"}`}>{name || "Unnamed"}{group ? ` +${d.members.length - 1}` : ""}</span>
                {kind && <span className="rigv2-kind-tag">{kind}</span>}
                <span className="rigv2-tile-sn mono">{sn}</span>
                {tag && <span className={`rigv2-up-tag ${tag.cls}`}>{tag.text}</span>}
                {sn && (
                  <DiscardControl
                    tag={tag}
                    confirming={confirmSn === sn}
                    busy={busySn === sn}
                    error={errors[sn] ?? null}
                    sentTo={emailedCountOf(a)}
                    onAsk={() => { setConfirmSn(sn); setErrors((e) => ({ ...e, [sn]: null })); }}
                    onConfirm={() => runDiscard(sn)}
                    onCancel={() => setConfirmSn(null)}
                  />
                )}
                {sn && <DownloadControl {...download.propsFor(sn, tag, canDownload)} />}
                {d?.status === "deleted" && <span className="rigv2-dlv deleted">{d.review?.state === "left_out" ? "Left out: never sent" : "Deleted"}</span>}
                {d?.status === "review" && <span className="rigv2-dlv deleted">Waiting for review: nothing is sent before Ivin approves it</span>}
                {/* A guest delete's answer stays when the capture's lines go (the last guest deleted: the capture is deleted). */}
                {canDeleteGuest && (!d || d.status === "deleted" || d.status === "review") && guestDeleteNotesOf(delNotes, sn).map(([k, note]) => (
                  <span key={k} className="rigv2-dlv-note">{note}</span>
                ))}
                {d && d.status !== "deleted" && d.status !== "review" && d.members.map((m) => {
                  const key = `${sn}|${m.rosterEntryId}`;
                  const guardian = blocked.has(m.rosterEntryId);
                  // no_email too: the server reads the roster when sending, so a
                  // fixed address goes out (and a missing one says so again).
                  // Never to a guest under 18 before the guardian check.
                  const sendable = canSend && !guardian && (m.status === "ready" || m.status === "sent" || m.status === "no_email");
                  return (
                    <React.Fragment key={m.rosterEntryId}>
                      <MemberLine m={m} name={group ? memberNameOf(a, m.rosterEntryId) || "Guest" : ""} guardian={guardian} />
                      {/* Admins: the page this guest sees, through an admin view link (GuestPageButton.jsx). */}
                      {canOpenGuestPage && hasGuestPage(m) && (
                        <GuestPageButton jobId={jobId} rosterEntryId={m.rosterEntryId} name={group ? memberNameOf(a, m.rosterEntryId) || "Guest" : ""} />
                      )}
                      {sendable && (
                        <button type="button" className="rigv2-btn small" disabled={Boolean(busy[key]) || Boolean(bulk && !bulk.finished)} onClick={() => sendMember(a, m)}>
                          {busy[key] ? "Sending…" : m.status === "sent" ? "Send again" : "Send to guest"}
                        </button>
                      )}
                      {notes[key] && <span className="rigv2-dlv-note">{notes[key]}</span>}
                      {/* A page that ended (the guest's own delete, a staff delete, or its time) took their details with it. */}
                      {canDeleteGuest && m.status === "ended" && delNotes[key] && <span className="rigv2-dlv-note">{delNotes[key]}</span>}
                      {canDeleteGuest && m.status !== "ended" && (
                        <GuestDeleteControl
                          open={delKey === key}
                          typed={delKey === key ? delTyped : ""}
                          busy={delBusy === key}
                          note={delNotes[key] ?? ""}
                          onOpen={() => { setDelKey(key); setDelTyped(""); setDelNotes((n) => ({ ...n, [key]: "" })); }}
                          onType={setDelTyped}
                          onConfirm={() => runGuestDelete(key, m.rosterEntryId)}
                          onCancel={() => { setDelKey(null); setDelTyped(""); }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
